import { Component, inject, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatActionList, MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminScript, AdminService, AdminScriptDiff, DiffGranularity } from '../../../../service/admin.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Script } from '../../../../service/script.service';
import { ListItemDisplayComponent } from '../../../../helper/script-editor/list-item-display/list-item-display.component';
import { MatInputModule } from '@angular/material/input';
@Component({
  selector: 'app-admin-script-review',
  imports: [
    MatProgressSpinnerModule, 
    MatCardModule, 
    MatChipsModule, 
    MatDivider, 
    MatSelectModule, 
    MatFormFieldModule, 
    FormsModule, 
    MatActionList, 
    ListItemDisplayComponent, 
    MatIconModule, 
    MatButtonModule, 
    NgClass, 
    MatInputModule,
    MatListModule,
    MatTooltipModule,
    RouterLink
  ],
  templateUrl: './admin-script-review.component.html',
  styleUrl: './admin-script-review.component.less'
})
export class AdminScriptReviewComponent implements AfterViewInit {
  @ViewChild('lineNumbersDiv') lineNumbersDivRef!: ElementRef<HTMLDivElement>;
  @ViewChild('scriptContentContainer') scriptContentContainerRef!: ElementRef<HTMLDivElement>;
  
  adminService = inject(AdminService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  snackBar = inject(MatSnackBar);
  script?: AdminScript | null = null;
  diff?: AdminScriptDiff | null = null;
  selectedCompareVersion: number = 0;
  availableVersions: { version: number; label: string }[] = [];
  currentVersion?: number;

  staffComment: string = '';

  // Diff mode selection
  diffMode: DiffGranularity = 'char';
  diffModes: { value: DiffGranularity; label: string }[] = [
    { value: 'char', label: 'Character' },
    { value: 'word', label: 'Word' },
    { value: 'line', label: 'Line' }
  ];
  private static readonly DIFF_MODE_STORAGE_KEY = 'adminDiffMode';

  ngOnInit() {
    // Load saved diff mode preference
    try {
      const savedMode = localStorage.getItem(AdminScriptReviewComponent.DIFF_MODE_STORAGE_KEY) as DiffGranularity | null;
      if (savedMode === 'char' || savedMode === 'word' || savedMode === 'line') {
        this.diffMode = savedMode;
      }
    } catch {}

    this.route.params.subscribe((params) => {
      const scriptId = params['script_id'];
      const version = params['version'] ? parseInt(params['version']) : undefined;
      this.currentVersion = version;
      
      this.adminService.getScript(scriptId, version).subscribe((script: AdminScript|null) => {

        this.script = script;
        this.staffComment = this.getCurrentVersionComment('staff_comment') || '';
        if (script && script.versions) {
          this.setupVersionComparison(script);
          this.updateScriptDataForCurrentVersion();
        }
        console.log(this.script);
        
        // Update line numbers after script data is loaded
        setTimeout(() => {
          this.updateLineNumbers();
        }, 100);

        // Get AI Rating
        this.adminService.getAIRating(scriptId, version ?? -1).subscribe((data: any) => {
          console.log('AI Rating API Response:', data);
          this.pendingAiRating = false;
          if (data.code == 200) {
            console.log('AI Rating data before assignment:', data.rating);
            // Handle both single rating object and array of ratings
            if (data.rating && !Array.isArray(data.rating)) {
              // If it's a single rating object, wrap it in an array
              this.aiRating = [data.rating];
            } else {
              // If it's already an array or null/undefined, use as is
              this.aiRating = data.rating || [];
            }
            console.log('AI Rating after assignment:', this.aiRating);
            console.log('AI Rating type:', typeof this.aiRating);
            console.log('AI Rating is array:', Array.isArray(this.aiRating));
            console.log('AI Rating length:', this.aiRating?.length);
          } else {
            console.log('AI Rating error:', data.message);
            this.aiRatingError = data.message;
          }
        });
      });
    });
  }

  ngAfterViewInit() {
    // Update line numbers after view is initialized
    setTimeout(() => {
      this.updateLineNumbers();
    }, 100);
  }

  aiRating: { label: string, description: string, line: number }[] = [];
  aiRatingError: string = '';
  pendingAiRating: boolean = true;

  onScriptContentScroll() {
    const scriptContentContainer = this.scriptContentContainerRef?.nativeElement;
    const lineNumbersDiv = this.lineNumbersDivRef?.nativeElement;
    
    if (scriptContentContainer && lineNumbersDiv) {
      lineNumbersDiv.scrollTop = scriptContentContainer.scrollTop;
    }
  }

  private setupVersionComparison(script: AdminScript) {
    // Create available versions list
    this.availableVersions = script.versions!.map((version, index) => ({
      version: version.version,
      label: `Version ${version.version} (${version.created_at.split(' ')[0]})`
    }));

    // Set default comparison to previous version (if available)
    if (script.versions!.length > 1) {
      this.selectedCompareVersion = script.versions![script.versions!.length - 2].version;
    } else {
      this.selectedCompareVersion = script.versions![0].version;
    }

    this.generateDiff();
  }

  onVersionChange() {
    this.generateDiff();
  }

  onDiffModeChange() {
    // Save preference
    try {
      localStorage.setItem(AdminScriptReviewComponent.DIFF_MODE_STORAGE_KEY, this.diffMode);
    } catch {}
    this.generateDiff();
  }

  private updateScriptDataForCurrentVersion() {
    if (!this.script || !this.script.versions) return;

    // Get the current version data
    const currentVersion = this.currentVersion 
      ? this.script.versions.find(v => v.version === this.currentVersion)
      : this.script.versions[this.script.versions.length - 1];

    if (!currentVersion) return;

    // Parse the script data and update the script properties
    const scriptData = JSON.parse(currentVersion.content);
    
    // Update script properties with the current version data
    this.script.title = scriptData.title || '';
    this.script.categories = scriptData.categories || [];
    this.script.content = scriptData.content || '';
    this.script.characters = scriptData.characters || [];
    this.script.suggestions = scriptData.suggestions || [];
    this.script.backdrop = scriptData.backdrop || this.script.backdrop;
  }

  private generateDiff() {
    if (!this.script || !this.script.versions) return;

    // Use the currently viewed version, or the latest if no specific version is selected
    const currentVersion = this.currentVersion 
      ? this.script.versions.find(v => v.version === this.currentVersion)
      : this.script.versions[this.script.versions.length - 1];
    
    const compareVersion = this.script.versions.find(v => v.version === this.selectedCompareVersion);

    if (!currentVersion || !compareVersion) return;

    // Parse the script data from JSON strings
    const currentScriptData = JSON.parse(currentVersion.content);
    const compareScriptData = JSON.parse(compareVersion.content);
    
    // Create Script objects for comparison
    const currentScript: Script = {
      ...this.script,
      content: currentScriptData.content || '',
      characters: currentScriptData.characters || [],
      backdrop: currentScriptData.backdrop || this.script.backdrop,
      suggestions: currentScriptData.suggestions || []
    };
    
    const compareScript: Script = {
      ...this.script,
      title: compareScriptData.title || this.script.title,
      categories: compareScriptData.categories || this.script.categories,
      content: compareScriptData.content || '',
      characters: compareScriptData.characters || [],
      backdrop: compareScriptData.backdrop || this.script.backdrop,
      suggestions: compareScriptData.suggestions || []
    };
    
    this.diff = this.adminService.getDiff(compareScript, currentScript, this.diffMode);
    
    // Update line numbers after diff is generated
    setTimeout(() => {
      this.updateLineNumbers();
    }, 100);
  }

  private updateLineNumbers() {
    const scriptContentContainer = this.scriptContentContainerRef?.nativeElement;
    const lineNumbersDiv = this.lineNumbersDivRef?.nativeElement;
    
    if (!scriptContentContainer || !lineNumbersDiv) {
      return;
    }
    
    // Get the content to count lines
    let content = '';
    let lineTypes: string[] = [];
    
    if (this.diff) {
      content = this.diff.lines.map(line => line.content).join('\n');
      lineTypes = this.diff.lines.map(line => line.type);
    } else if (this.script) {
      content = this.script.content || '';
      // For non-diff mode, all lines are considered unchanged
      lineTypes = content.split('\n').map(() => 'unchanged');
    }
    
    // Count lines in content
    const lines = content.split('\n');
    const lineCount = lines.length;
    
    // Calculate line heights and wrapping
    const lineHeights = this.calculateLineHeights(scriptContentContainer, lines);
    
    // Generate line numbers HTML with proper spacing
    // Only count lines that are unchanged or added
    let lineNumbersHtml = '';
    let lineNumberCounter = 1;
    
    for (let i = 0; i < lineCount; i++) {
      const lineHeight = lineHeights[i];
      const height = lineHeight > 1 ? `${lineHeight * 1.5}em` : '1.5em';
      
      if (lineTypes[i] === 'removed') {
        // For removed lines, create an empty div with the same height but no line number
        lineNumbersHtml += `<div class="line-number" style="height: ${height}; min-height: ${height}; color: transparent;">&nbsp;</div>`;
      } else {
        // For unchanged and added lines, show the line number
        lineNumbersHtml += `<div class="line-number" style="height: ${height}; min-height: ${height};">${lineNumberCounter}</div>`;
        lineNumberCounter++;
      }
    }
    
    // Update line numbers container
    lineNumbersDiv.innerHTML = lineNumbersHtml;
    
    // Apply dynamic height styling to script lines
    this.applyDynamicLineHeights(scriptContentContainer, lineHeights);
    
    // Sync scroll position with script content container
    lineNumbersDiv.scrollTop = scriptContentContainer.scrollTop;
  }

  private applyDynamicLineHeights(container: HTMLDivElement, lineHeights: number[]) {
    // Find all script line elements
    const scriptLines = container.querySelectorAll('.script-line');
    
    // Apply dynamic heights to script lines
    scriptLines.forEach((lineElement, index) => {
      if (index < lineHeights.length) {
        const lineHeight = lineHeights[index];
        const height = lineHeight > 1 ? `${lineHeight * 1.5}em` : '1.5em';
        (lineElement as HTMLElement).style.height = height;
        (lineElement as HTMLElement).style.minHeight = height;
      }
    });
  }

  private calculateLineHeights(container: HTMLDivElement, lines: string[]): number[] {
    const lineHeights: number[] = [];
    const containerStyle = window.getComputedStyle(container);
    const fontSize = parseFloat(containerStyle.fontSize);
    const fontFamily = containerStyle.fontFamily;
    const padding = parseFloat(containerStyle.paddingLeft) + parseFloat(containerStyle.paddingRight);
    const availableWidth = container.clientWidth - padding;
    
    // Create a temporary element to measure text width
    const tempElement = document.createElement('div');
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.whiteSpace = 'pre-wrap';
    tempElement.style.wordWrap = 'break-word';
    tempElement.style.fontSize = fontSize + 'px';
    tempElement.style.fontFamily = fontFamily;
    tempElement.style.lineHeight = '1.5';
    tempElement.style.width = availableWidth + 'px';
    tempElement.style.boxSizing = 'border-box';
    tempElement.style.padding = '0';
    tempElement.style.margin = '0';
    tempElement.style.border = 'none';
    document.body.appendChild(tempElement);
    
    try {
      for (const line of lines) {
        tempElement.textContent = line;
        
        // Get the actual height of the element after text wrapping
        const elementHeight = tempElement.offsetHeight;
        const singleLineHeight = fontSize * 1.5; // 1.5em line height
        const linesNeeded = Math.ceil(elementHeight / singleLineHeight);
        
        lineHeights.push(Math.max(1, linesNeeded));
      }
    } finally {
      document.body.removeChild(tempElement);
    }
    
    return lineHeights;
  }

  @HostListener('window:resize')
  onWindowResize() {
    // Recalculate line numbers when window is resized
    setTimeout(() => {
      this.updateLineNumbers();
    }, 100); // Small delay to ensure layout is complete
  }

  // Version management methods
  getVersionStatus(version: any): string {
    const status = version.status;
    if (status & 8) return 'Published';
    if (status & 4) return 'Rejected' + (status & 16 ? ' (Previously published)' : '');
    if (status & 1) return 'Pending';
    return 'Superseeded';
  }

  getVersionActions(version: any): { action: string; label: string; disabled: boolean; color: string }[] {
    const status = version.status;
    const actions = [
      { action: 'publish', label: 'Publish', disabled: version.is_draft || (status & 8) == 8, color: 'primary' },
      { action: 'reject', label: 'Reject', disabled: version.is_draft || (status & 4) == 4, color: 'warn' }
    ];
    return actions;
  }

  onVersionAction(version: number, action: string) {
    if (!this.script) return;

    // If AI rating has warnings, confirm before publishing
    if (action === 'publish' && this.hasAiRating()) {
      const proceed = window.confirm(
        `The AI flagged ${this.aiRating?.length || 0} potential issue(s) in this script.\n\n` +
        `Are you sure you want to publish anyway?`
      );
      if (!proceed) {
        return;
      }
    }

    this.adminService.performVersionAction(this.script.id, version, action as any).subscribe({
      next: (response) => {
        console.log(`Version ${action}ed successfully`);
        // Reload the script to get updated status
        this.adminService.getScript(this.script!.id, this.currentVersion).subscribe((script: AdminScript|null) => {
          this.script = script;
          this.staffComment = this.getCurrentVersionComment('staff_comment') || '';
          if (script && script.versions) {
            this.setupVersionComparison(script);
          }
        });
      },
      error: (error) => {
        console.error(`Failed to ${action} version:`, error.error?.message || 'Unknown error');
      }
    });
  }

  getCurrentVersionComment(commentType: 'author_comment' | 'staff_comment'): string | null {
    if (!this.script || !this.script.versions) return null;

    const currentVersion = this.currentVersion 
      ? this.script.versions.find(v => v.version === this.currentVersion)
      : this.script.versions[this.script.versions.length - 1];

    return currentVersion?.[commentType] || null;
  }

  isCurrentlyViewedVersion(version: number): boolean {
    let result: boolean;
    if (!this.currentVersion) {
      // If no specific version is selected, we're viewing the latest
      result = version == this.script?.versions?.[this.script.versions.length - 1]?.version;
    } else {
      result = version == this.currentVersion;
    }
    return result;
  }

  getCurrentVersionNumber(): number {
    if (!this.script || !this.script.versions) return 0;

    if (!this.currentVersion) {
      // If no specific version is selected, we're viewing the latest
      return this.script.versions[this.script.versions.length - 1].version;
    }
    return this.currentVersion;
  }

  onViewVersion(version: number) {
    if (!this.script) return;
    
    if (version === this.script.versions![this.script.versions!.length - 1].version) {
      // Viewing latest version - no version in URL
      this.router.navigate(['/admin/scripts', this.script.id]);
    } else {
      // Viewing specific version
      this.router.navigate(['/admin/scripts', this.script.id, version]);
    }
  }

  onSaveStaffComment() {
    if (!this.script || !this.script.versions) return;

    let version = this.currentVersion;
    if (!version) {
      version = this.script.versions[this.script.versions.length - 1].version;
    };

    this.adminService.saveStaffComment(this.script.id, version, this.staffComment).subscribe({
      next: (response) => {
        console.log('Staff comment saved successfully');
        this.snackBar.open('Staff comment saved successfully');
      },
      error: (error) => {
        console.error('Failed to save staff comment:', error);
        this.snackBar.open('Failed to save staff comment', 'Close', { duration: 3000 });
      }
    });
  }

  // AI Rating helper methods
  getWarningsForLine(lineNumber: number): { label: string, description: string }[] {
    return Array.isArray(this.aiRating) ? this.aiRating.filter(rating => rating.line === lineNumber) : [];
  }

  hasWarnings(lineNumber: number): boolean {
    return Array.isArray(this.aiRating) && this.aiRating.some(rating => rating.line === lineNumber);
  }

  hasAiRating(): boolean {
    console.log('hasAiRating called - aiRating:', this.aiRating);
    console.log('aiRating is array:', Array.isArray(this.aiRating));
    console.log('aiRating length:', this.aiRating?.length);
    const result = Array.isArray(this.aiRating) && this.aiRating.length > 0;
    console.log('hasAiRating result:', result);
    return result;
  }

  getWarningTooltip(lineNumber: number): string {
    const warnings = this.getWarningsForLine(lineNumber);
    return warnings.map(warning => `${warning.label || 'Warning'}: ${warning.description || 'No description available'}`).join('\n');
  }

  // Get the actual line number for display (accounting for diff mode)
  getActualLineNumber(index: number): number {
    if (this.diff) {
      // In diff mode, we need to map the diff line index to the actual script line number
      // The AI rating line numbers correspond to the original script content
      // We need to count only the lines that represent actual script content (not removed lines)
      let scriptLineNumber = 1;
      for (let i = 0; i <= index; i++) {
        if (this.diff.lines[i].type !== 'removed') {
          scriptLineNumber++;
        }
      }
      return scriptLineNumber - 1; // Subtract 1 because we start counting from 1
    } else {
      // In non-diff mode, line numbers are straightforward
      return index + 1;
    }
  }

  scrollToLine(lineNumber: number) {
    const scriptContentContainer = this.scriptContentContainerRef?.nativeElement;
    if (!scriptContentContainer) return;

    // Find the line element to scroll to
    const lineElements = scriptContentContainer.querySelectorAll('.script-line');
    let targetLineElement: HTMLElement | null = null;
    
    if (this.diff) {
      // In diff mode, find the line that corresponds to the target line number
      let currentLineNumber = 1;
      for (let i = 0; i < lineElements.length; i++) {
        if (this.diff.lines[i].type !== 'removed') {
          if (currentLineNumber === lineNumber) {
            targetLineElement = lineElements[i] as HTMLElement;
            break;
          }
          currentLineNumber++;
        }
      }
    } else {
      // In non-diff mode, directly use the line number
      if (lineNumber <= lineElements.length) {
        targetLineElement = lineElements[lineNumber - 1] as HTMLElement;
      }
    }

    if (targetLineElement) {
      targetLineElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Add a temporary highlight effect
      targetLineElement.style.backgroundColor = 'var(--mat-sys-error)';
      targetLineElement.style.color = 'white';
      setTimeout(() => {
        targetLineElement!.style.backgroundColor = '';
        targetLineElement!.style.color = '';
      }, 2000);
    }
  }
}
